import { css } from 'styled-components';
import { Spacing24 } from 'autogen/design-tokens/tokens';
import {
    type ConnectedContentProductDisplayModel,
    CustomActionDiscriminator,
    PageActionDiscriminator
} from 'autogen/swagger/all';
import ContentCard from 'components/ContentCard';
import type { ContentCardProps } from 'components/ContentCard/types';
import { mapResponsiveImageModelToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import DynamicItemCarousel from 'components/generic/ScrollSnapSlider/components/DynamicItemCarousel';
import openBambuser from 'components/live/openBambuser';
import { guardIsPageActionOfTypeLinkAction } from 'components/shared/Routing/components/shared/helpers';
import type { PageActionModel } from 'components/shared/Routing/PageAction';
import ConnectedContentSurveyItem from 'components/surveySystem/components/ConnectedContentSurveyItem';
import useVimeoQuickView from 'components/video/hooks/useVimeoQuickView';
import type { IVideo } from 'components/video/shared/types';
import { stringToKey } from 'modules/helpers/strings';
import { breakpoint, sizes } from 'variables';

const scrollContainerCss = css`
    && {
        padding: 0 ${({ theme }) => theme.Spacing16};
        margin: 0 -${({ theme }) => theme.Spacing16};
        scroll-padding-inline-end: ${({ theme }) => theme.Spacing16};
        scroll-padding-inline-start: ${({ theme }) => theme.Spacing16};

        ${breakpoint.up('1025px')} {
            padding: 0;
            margin: 0;
        }
    }
`;

export interface IProductDisplayConnectedContent {
    model: ConnectedContentProductDisplayModel[];
}

function getVideoAction(
    pageAction: PageActionModel | undefined,
    openBambuser: (liveEventId: number) => Promise<void>,
    openVimeoQuickView: (state: { isOpen: boolean; video?: IVideo }) => void
) {
    if (!pageAction) return null;
    return PageActionDiscriminator(
        pageAction,
        (customAction) =>
            CustomActionDiscriminator(
                customAction.action,
                () => null,
                () => null,
                (LiveEventCustomAction) => async () => await openBambuser(LiveEventCustomAction.liveEventId),
                () => null,
                (VimeoCustomAction) => async () =>
                    await openVimeoQuickView({
                        isOpen: true,
                        video: {
                            id: VimeoCustomAction.vimeoId,
                            platform: 'Vimeo',
                            url: VimeoCustomAction.videoUrl
                        }
                    }),
                () => null
            ),
        () => null,
        () => null
    );
}

function ProductDisplayConnectedContent(props: IProductDisplayConnectedContent) {
    const { model } = props;
    const [, openVimeoQuickView] = useVimeoQuickView();

    return (
        <DynamicItemCarousel
            gapSize={Spacing24}
            items={model}
            scrollContainerCss={scrollContainerCss}
            itemRenderer={(item: ConnectedContentProductDisplayModel, index) => {
                const { heading, layoutType } = item;
                const keySuffix = heading ? stringToKey(heading) : index;
                const { pageAction } = item.link || {};
                const videoAction = getVideoAction(pageAction, openBambuser, openVimeoQuickView);
                const linkAction = guardIsPageActionOfTypeLinkAction(item.link?.pageAction);
                const link: ContentCardProps['link'] | undefined =
                    item.link && linkAction
                        ? {
                              action: linkAction,
                              label: item.link.linkText
                          }
                        : undefined;
                const isVideo = videoAction !== null;

                if (item.surveyQuestion) {
                    return (
                        <ConnectedContentSurveyItem
                            key={`${layoutType}_${keySuffix}`}
                            model={item}
                            type="ConnectedContentProductDisplay"
                        />
                    );
                }
                return (
                    <ContentCard
                        key={`${layoutType}_${keySuffix}`}
                        isTransparent
                        image={{
                            ...mapResponsiveImageModelToImageProps(item.image),
                            transformType: 'WidthScale',
                            aspectRatio: '1/1'
                        }}
                        link={link}
                        heading={item.heading}
                        subheading={item.layoutText}
                        onClickWrapper={isVideo ? () => videoAction() : undefined}
                        showPlayIcon={isVideo}
                    />
                );
            }}
        />
    );
}

export default ProductDisplayConnectedContent;
